import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Input,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  TextFieldProps,
  SxProps,
} from '@mui/material';
import { InputProps } from '@mui/material/Input';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { FieldInputProps, FieldMetaState } from 'react-final-form';
import PhoneInput from 'react-phone-input-2';
import jpPhoneLocalization from 'react-phone-input-2/lang/jp.json';
import 'react-phone-input-2/lib/style.css';

import { LinkifyLabel } from 'components/LinkifyLabel/LinkifyLabel';
import { GoogleMapsPlaceInput } from 'components/GoogleMapsPlaceInput/GoogleMapsPlaceInput';

import styles from './formHelpers.module.css';

const noTopMarginStyle = {
  marginTop: 0,
};

const whiteSpaceStyle = {
  whiteSpace: 'pre-line',
};

// This file has helper functions for using Material UI with redux form

export function WrappedTextField<FieldValue>({
  label,
  input,
  fullWidth,
  required,
  meta: { touched, invalid, error },
  helperText,
  multiline,
  format,
  ...custom
}: {
  label: string;
  input: FieldInputProps<FieldValue>;
  fullWidth?: boolean;
  required?: boolean;
  meta: FieldMetaState<FieldValue>;
  helperText?: string;
  multiline?: boolean;
  format?: string;
} & InputProps) {
  const { t, i18n } = useTranslation();

  React.useEffect(() => {
    // If this is a phone input and the initial value is empty, set it to the default value based on language
    if (custom.type === 'phone' && !input.value) {
      input.onChange(`+${i18n.language === 'ja-JP' ? '81' : '1'}`);
    }
  }, []);

  return (
    <FormControl
      variant="standard"
      error={touched && invalid}
      fullWidth={fullWidth}
      required={required}
      margin="normal"
    >
      <FormLabel htmlFor="bootstrap-input" sx={whiteSpaceStyle}>
        <LinkifyLabel>{label}</LinkifyLabel>
      </FormLabel>
      {custom.type === 'phone' ? (
        <PhoneInput
          placeholder={i18n.language === 'ja-JP' ? '81 9012345678' : '1 800 123 4567'}
          masks={{ jp: '...........' }}
          containerClass={styles['phone-input']}
          preferredCountries={i18n.language === 'ja-JP' ? ['jp', 'us'] : ['us', 'jp']}
          localization={i18n.language === 'ja-JP' ? jpPhoneLocalization : undefined}
          value={(input.value as any)?.replace('+', '')}
          onChange={(value: string, data: { dialCode: string }) => {
            if (data && !value?.startsWith(data.dialCode)) {
              input.onChange(`+${data.dialCode}${value}`);
            } else if (value && !value?.startsWith('+')) {
              input.onChange(`+${value}`);
            } else {
              input.onChange(value);
            }
          }}
          enableClickOutside={false}
          searchPlaceholder={t('Search')}
          searchNotFound={t('No Results')}
          enableSearch
          isValid={() => {
            if (touched && error) {
              return false;
            }
            return true;
          }}
        />
      ) : (
        <>
          {custom.type === 'date' || custom.type === 'datetime-local' ? (
            <img className={styles['calendar-icon']} src="/static/images/ic_calendar.svg" />
          ) : undefined}
          {multiline ? (
            <OutlinedInput
              multiline={multiline}
              rows={8}
              style={noTopMarginStyle}
              {...input}
              {...custom}
            />
          ) : (
            <Input style={noTopMarginStyle} {...input} {...custom} />
          )}
        </>
      )}
      {WrappedFormHelper({ touched, error, helperText })}
    </FormControl>
  );
}

export const WrappedCheckbox = ({
  label,
  input,
  fullWidth,
  required,
  meta: { touched, invalid, error },
  helperText,
}: {
  label: string;
  input: FieldInputProps<boolean>;
  fullWidth?: boolean;
  required?: boolean;
  meta: FieldMetaState<boolean>;
  helperText?: string;
} & InputProps) => {
  return (
    <FormControl
      variant="standard"
      error={touched && invalid}
      fullWidth={fullWidth}
      required={required}
      margin="normal"
    >
      <FormControlLabel
        style={{ marginTop: 10, marginBottom: 10 }}
        label={
          <Typography
            style={{
              color: 'rgba(0, 0, 0, 0.54)',
              fontSize: '1rem',
              fontWeight: 400,
              whiteSpace: 'pre-line',
            }}
          >
            <LinkifyLabel>{label}</LinkifyLabel>
          </Typography>
        }
        control={<Checkbox color="primary" {...input} checked={input.value} />}
      />
      {WrappedFormHelper({ touched, error, helperText })}
    </FormControl>
  );
};

export const WrappedFormHelper = ({
  touched,
  error,
  helperText,
}: {
  touched?: boolean;
  error: string;
  helperText?: string;
}) => {
  const { t } = useTranslation();

  let text = touched && t(error);
  if (!text) {
    text = helperText || '';
  }

  return text ? <FormHelperText>{text}</FormHelperText> : null;
};

export function WrappedSelectField<FieldValue>({
  input,
  label,
  meta: { touched, error },
  children,
  className,
  sx,
  fullWidth,
  required,
  ...custom
}: {
  label: string;
  input: FieldInputProps<FieldValue>;
  meta: FieldMetaState<FieldValue>;
  sx?: SxProps;
  className?: string;
  fullWidth?: boolean;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <FormControl
      variant="standard"
      sx={sx}
      fullWidth={fullWidth}
      error={touched && !!error}
      required={required}
      margin="normal"
    >
      <FormLabel htmlFor={input.name} sx={whiteSpaceStyle}>
        <LinkifyLabel>{label}</LinkifyLabel>
      </FormLabel>
      <Select variant="standard" style={noTopMarginStyle} {...input} {...custom}>
        {children}
      </Select>
      {WrappedFormHelper({ touched, error })}
    </FormControl>
  );
}

export function WrappedMaterialTextField<FieldValue>({
  input,
  label,
  meta: { touched, error },
  className,
  fullWidth,
  required,
  ...custom
}: {
  label: string;
  input: FieldInputProps<FieldValue>;
  meta: FieldMetaState<FieldValue>;
  className?: string;
  fullWidth?: boolean;
  required?: boolean;
} & TextFieldProps) {
  return (
    <FormControl
      variant="outlined"
      className={className}
      fullWidth={fullWidth}
      error={touched && !!error}
      required={required}
      margin="dense"
    >
      <FormLabel htmlFor="bootstrap-input" sx={whiteSpaceStyle}>
        <LinkifyLabel>{label}</LinkifyLabel>
      </FormLabel>
      <TextField size="small" variant="outlined" style={noTopMarginStyle} {...input} {...custom} />
      {WrappedFormHelper({ touched, error })}
    </FormControl>
  );
}

// googlePlaceInputNormalizer is a hack to prevent the Input's onBlur handler propagating up to the Field
// and re-setting our redux form place object ({locationName: string, placeId: string}) to a string. The
// normalizer discards any modifications that have type=string.
export const googlePlaceInputNormalizer = (value: any, previousValue: any) => {
  if (typeof value === 'string') {
    return previousValue;
  }

  return value;
};

type Location = {
  locationName: string;
  placeId: string;
};

export const WrappedGooglePlaceInput = ({
  input,
  label,
  meta: { touched, error },
  className,
  fullWidth,
  required,
}: {
  label: string;
  input: FieldInputProps<Location>;
  meta: FieldMetaState<Location>;
  className?: string;
  fullWidth?: boolean;
  required?: boolean;
}) => {
  const onChange = input.onChange;
  const handleInputChange = React.useMemo(() => {
    return (event: any) => onChange({ locationName: event.target.value, placeId: '' });
  }, [onChange]);

  const locationName = (input.value && (input.value.locationName as string)) || '';

  return (
    <GoogleMapsPlaceInput
      renderInput={(params: any) => {
        return (
          <FormControl
            variant="standard"
            className={className}
            fullWidth={fullWidth}
            error={touched && !!error}
            required={required}
            margin="normal"
          >
            <FormLabel {...params.InputLabelProps} htmlFor="bootstrap-input" sx={whiteSpaceStyle}>
              <LinkifyLabel>{label}</LinkifyLabel>
            </FormLabel>
            <Input
              {...params.InputProps}
              inputProps={{ ...params.inputProps }}
              style={noTopMarginStyle}
              {...input}
              value={locationName}
              onChange={handleInputChange}
            />
            {WrappedFormHelper({ touched, error })}
          </FormControl>
        );
      }}
      onOptionSelected={(selectedOption) => {
        if (!selectedOption) {
          input.onChange(null);
        } else {
          input.onChange({
            locationName: selectedOption.structured_formatting.main_text,
            placeId: selectedOption.place_id,
          });
        }
      }}
      value={locationName}
    />
  );
};

export const GooglePlaceInputFieldProps = {
  component: WrappedGooglePlaceInput,
  parse: googlePlaceInputNormalizer,
};
